import {ArrowBothIcon, ColumnsIcon, ListUnorderedIcon, ProjectIcon, RowsIcon} from '@primer/octicons-react'
import {AriaAlert, Label, Spinner} from '@primer/react'
import {useMemo} from 'react'

import {useSetVisibleField, useVisibleFieldIdentifiers} from '../../contexts/views/VisibleFieldsProvider'
import {useFields} from '../../contexts/views/FieldsProvider'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData, FieldIdentifier} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import {useSavedViewOptions} from './use-saved-view-options'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

// The repository scaffold still groups by label. Its wrapper exposes no axis writers.
const TEMP_GROUP_BY = FieldIdentifier.LABEL
const noopSelect = () => {}

/**
 * The issues saved-view options menu. Projects the view definition into groupable and
 * attributable field lists; layout comes from the URL via {@link useSavedViewOptions}.
 */
export function SavedViewOptionsMenu() {
  const view = useFields()
  return <SavedViewOptions fields={view} />
}

interface SavedViewOptionsProps {
  fields: FieldsData | undefined
  /** True while the field definitions are being fetched asynchronously. */
  isPending?: boolean
  /** True when the async fetch for the field definitions failed. */
  isError?: boolean
}

export function SavedViewOptions({fields: view, isPending = false, isError = false}: SavedViewOptionsProps) {
  const {layout, setLayout} = useSavedViewOptions()
  const visibleFieldIdentifiers = useVisibleFieldIdentifiers()
  const setVisibleField = useSetVisibleField()
  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  return (
    <ViewOptionsMenu triggerLabel="View options" title="View options">
      <ViewOptionsMenu.Layout
        label="Layout"
        trailing={layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={layout}
        onSelect={setLayout}
      />

      {layout === 'board' && (
        <>
          <ViewOptionsMenu.Group
            label="Configuration"
            status={
              isPending ? (
                <div className={styles.status}>
                  <Spinner srText="Loading view options" />
                </div>
              ) : isError ? (
                <AriaAlert className={styles.status}>Unable to load view options</AriaAlert>
              ) : null
            }
          >
            {view && (
              <>
                <ViewOptionsMenu.SingleSelect
                  icon={ColumnsIcon}
                  label="Column by"
                  placeholder="None"
                  fields={groupFields}
                  value={TEMP_GROUP_BY}
                  onSelect={noopSelect}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={RowsIcon}
                  label="Swimlane by"
                  placeholder="None"
                  fields={groupFields}
                  value={null}
                  onSelect={noopSelect}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={SortByIcon}
                  label="Sort by"
                  placeholder="None"
                  fields={sortFields}
                  value={null}
                  onSelect={noopSelect}
                />
              </>
            )}
          </ViewOptionsMenu.Group>
          {view && (
            <ViewOptionsMenu.ToggleGroup
              label="Visible fields"
              fields={attributeFields}
              selectedIds={visibleFieldIdentifiers}
              onToggle={setVisibleField}
            />
          )}
        </>
      )}
    </ViewOptionsMenu>
  )
}
